import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, RefreshCw, Check, AlertCircle, UserPlus, Mail, ChevronDown, ChevronUp } from 'lucide-react';
import { fetchSyncPreview, importSyncDoctors } from '../services/authService';

export default function CsvSyncModal({ onClose, onImported }) {
  const [step,     setStep]     = useState('loading'); // loading | preview | importing | done | error
  const [preview,  setPreview]  = useState(null);
  const [doctors,  setDoctors]  = useState([]);        // [{ name, email, selected }]
  const [results,  setResults]  = useState(null);
  const [error,    setError]    = useState('');
  const [showExisting, setShowExisting] = useState(false);

  // Load preview on mount
  useEffect(() => {
    fetchSyncPreview()
      .then(data => {
        setPreview(data);
        setDoctors(
          data.newDoctors.map(d => ({
            name:     d.name,
            email:    d.suggestedEmail,
            selected: true,
          }))
        );
        setStep('preview');
      })
      .catch(err => {
        setError(err.message || 'Failed to read CSV');
        setStep('error');
      });
  }, []);

  const toggleDoctor = (idx) => {
    setDoctors(prev => prev.map((d, i) => i === idx ? { ...d, selected: !d.selected } : d));
  };

  const updateEmail = (idx, email) => {
    setDoctors(prev => prev.map((d, i) => i === idx ? { ...d, email } : d));
  };

  const handleImport = async () => {
    const selected = doctors.filter(d => d.selected);
    if (selected.length === 0) return;

    setStep('importing');
    try {
      const res = await importSyncDoctors(selected.map(d => ({ name: d.name, email: d.email })));
      setResults(res);
      setStep('done');
      onImported();
    } catch (err) {
      setError(err.message || 'Import failed');
      setStep('error');
    }
  };

  const selectedCount = doctors.filter(d => d.selected).length;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 999999 }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col"
           onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-2.5">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <RefreshCw className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Sync Physicians from CSV</h2>
              {preview && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {preview.csvFile} · {preview.totalInCSV} doctors in CSV
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5">

          {/* Loading */}
          {step === 'loading' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-sm text-gray-500">Reading CSV file...</p>
            </div>
          )}

          {/* Importing */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-sm text-gray-500">Creating physician accounts...</p>
            </div>
          )}

          {/* Error */}
          {step === 'error' && (
            <div className="flex flex-col items-center justify-center py-8">
              <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
              <p className="text-sm font-medium text-gray-900 mb-1">Something went wrong</p>
              <p className="text-xs text-red-600 text-center">{error}</p>
            </div>
          )}

          {/* Preview */}
          {step === 'preview' && preview && (
            <div className="space-y-4">

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-gray-900">{preview.totalInCSV}</p>
                  <p className="text-xs text-gray-500">In CSV</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-gray-900">{preview.totalInDB}</p>
                  <p className="text-xs text-gray-500">In System</p>
                </div>
                <div className={`rounded-lg p-3 text-center ${preview.newCount > 0 ? 'bg-blue-50' : 'bg-emerald-50'}`}>
                  <p className={`text-lg font-bold ${preview.newCount > 0 ? 'text-blue-700' : 'text-emerald-700'}`}>
                    {preview.newCount}
                  </p>
                  <p className="text-xs text-gray-500">New Found</p>
                </div>
              </div>

              {/* No new doctors */}
              {preview.newCount === 0 && (
                <div className="text-center py-6">
                  <Check className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-900">All doctors are already in the system</p>
                  <p className="text-xs text-gray-500 mt-1">No new physicians to import from this CSV</p>
                </div>
              )}

              {/* New doctors list */}
              {preview.newCount > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-gray-800">
                      New Physicians ({preview.newCount})
                    </p>
                    <button
                      onClick={() => setDoctors(prev => prev.map(d => ({ ...d, selected: selectedCount < doctors.length })))}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      {selectedCount === doctors.length ? 'Deselect all' : 'Select all'}
                    </button>
                  </div>

                  {/* Note about emails */}
                  {/* <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                    <p className="text-xs text-amber-700">
                      ⚠️ Emails are auto-generated placeholders. Update with real emails before importing for production. Welcome email is only sent to real (non-placeholder) email addresses.
                    </p>
                  </div> */}

                  <div className="space-y-2">
                    {doctors.map((doc, idx) => (
                      <div
                        key={idx}
                        className={`border rounded-lg p-3 transition-colors ${
                          doc.selected ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex items-start space-x-2.5">
                          <button
                            onClick={() => toggleDoctor(idx)}
                            className={`w-4 h-4 rounded border flex-shrink-0 mt-0.5 flex items-center justify-center ${
                              doc.selected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                            }`}
                          >
                            {doc.selected && <Check className="w-3 h-3 text-white" />}
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                            <div className="flex items-center space-x-1.5 mt-1">
                              <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                              <input
                                type="email"
                                value={doc.email}
                                onChange={e => updateEmail(idx, e.target.value)}
                                className="flex-1 text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                                placeholder="Enter email address"
                              />
                            </div>
                            {/* {doc.email.endsWith('@physician.actelligence.in') && (
                              <p className="text-xs text-amber-600 mt-0.5">Placeholder email — no welcome email will be sent</p>
                            )} */}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Existing doctors (collapsible) */}
              {preview.totalInDB > 0 && (
                <div>
                  <button
                    onClick={() => setShowExisting(p => !p)}
                    className="flex items-center space-x-1.5 text-xs text-gray-500 hover:text-gray-700"
                  >
                    {showExisting ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    <span>{preview.totalInDB} existing physicians already in system</span>
                  </button>
                  {showExisting && preview.existingDoctors?.length > 0 && (
                    <div className="mt-2 max-h-40 overflow-y-auto border border-gray-100 rounded-lg">
                      {preview.existingDoctors.map((name, i) => (
                        <div key={i} className="flex items-center space-x-2 px-3 py-1.5 border-b border-gray-50 last:border-0">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                          <span className="text-xs text-gray-600">{name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

            </div>
          )}

          {/* Done */}
          {step === 'done' && results && (
            <div className="space-y-4">
              <div className="text-center py-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Check className="w-6 h-6 text-emerald-600" />
                </div>
                <p className="text-base font-semibold text-gray-900">Import Complete</p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-emerald-700">{results.created.length}</p>
                  <p className="text-xs text-gray-500">Created</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-amber-700">{results.skipped.length}</p>
                  <p className="text-xs text-gray-500">Skipped</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-red-700">{results.failed.length}</p>
                  <p className="text-xs text-gray-500">Failed</p>
                </div>
              </div>

              {results.created.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-2">Created accounts:</p>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {results.created.map((d, i) => (
                      <div key={i} className="bg-gray-50 rounded-lg px-3 py-2">
                        <p className="text-xs font-medium text-gray-900">{d.name}</p>
                        <p className="text-xs text-gray-500">{d.email}</p>
                        {d.emailSent ? (
                          <p className="text-xs text-blue-600 mt-0.5">✉ Sent a link to set their password</p>
                        ) : d.isPlaceholder ? (
                          <p className="text-xs text-gray-400 mt-0.5">No real email address — nothing sent</p>
                        ) : (
                          <p className="text-xs text-amber-600 mt-0.5">⚠ Email failed — it will be retried automatically</p>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Each physician sets their own password through a single-use link, so there are no
                    passwords to pass on. Assign hospitals via the 🏢 icon in User Management.
                  </p>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex-shrink-0">
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-white transition-colors">
            {step === 'done' ? 'Close' : 'Cancel'}
          </button>
          {step === 'preview' && preview?.newCount > 0 && (
            <button
              onClick={handleImport}
              disabled={selectedCount === 0}
              className="flex items-center space-x-2 px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              <span>Import Selected ({selectedCount})</span>
            </button>
          )}
        </div>

      </div>
    </div>,
    document.body
  );
}
